import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Info,
  XCircle,
} from "lucide-react";
import { Cabecera } from "@/components/propuesta/cabecera";
import { Aviso, Caja, Tile } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/asientos")({ component: Asientos });

// Los tipos se declaran aquí y no se infieren del contrato: ts-rest 3.x no
// propaga los esquemas de Zod 4 a través del router, así que sin esto cada
// `map` llega como `any`. Es lo mismo que hace la pantalla de cierre.
type Problema = {
  nivel: "error" | "aviso";
  codigo: string;
  mensaje: string;
  cuantos: number;
  ejemplos: string[];
};
type Entrega = {
  subDiario: string;
  archivo: string;
  usuario: string;
  lineas: number;
  desde: string | null;
  hasta: string | null;
  cuando: string;
  forzada: boolean;
};
type Moneda = {
  moneda: string;
  asientos: number;
  debe: number;
  haber: number;
  cuadra: boolean;
};
type Linea = {
  subDiario: string;
  numeroComprobante: string;
  fechaComprobante: string;
  codigoMoneda: string;
  cuentaContable: string;
  codigoAnexo: string | null;
  debeHaber: string;
  importe: number;
  tipoDocumento: string | null;
  numeroDocumento: string | null;
  codigoArea: string | null;
  glosaPrincipal: string;
  glosaDetalle: string;
};

// ===========================================================================
// La vista previa de CONCAR.
//
// Es el panel del lote del ERP del área, traído aquí: arriba las cifras que
// dicen si el archivo se puede entregar, debajo lo que está mal, y al final las
// líneas exactas que van a salir.
//
// POR QUÉ ESTA PANTALLA EXISTE
// -----------------------------
// El Excel de CONCAR llevaba meses construido y solo se podía conseguir
// escribiendo la URL de descarga a mano, o desde un botón al final del cierre.
// Nadie podía ver qué iba dentro antes de mandarlo, ni por qué faltaban
// asientos. En el ERP eso es la pantalla principal, la que se mira antes de
// tocar el botón, y es la diferencia entre entregar un archivo y confiar en él.
//
// MIRAR NO GASTA NUMERACIÓN. Esta pantalla lee el último número de comprobante
// sin consumirlo; solo la descarga reserva. Abrirla tres veces no deja huecos
// que después haya que explicarle a un auditor.
// ===========================================================================

function Asientos() {
  const { data, isPending } = api.asientos.useQuery(["asientos"]);
  const d = data?.status === 200 ? data.body : null;

  const dinero = (centimos: number) =>
    (centimos / 100).toLocaleString("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const monedas = (d?.porMoneda ?? []) as Moneda[];
  const puedeEntregar = d
    ? d.errores === 0 && d.cuadra && d.asientos > 0
    : false;
  const nombreMoneda = (m: string) => (m === "MN" ? "soles" : "dólares");

  return (
    <>
      <Cabecera
        migas={["Reportes"]}
        titulo="Excel para CONCAR"
        facetas={[{ clave: "Periodo", valor: d?.periodo ?? "—" }]}
        acciones={
          <Button
            size="sm"
            variant={puedeEntregar ? "default" : "outline"}
            asChild
          >
            <a href="/exportar/asientos.xlsx" download>
              <Download className="size-4" />
              Descargar el Excel
            </a>
          </Button>
        }
      />

      <div className="space-y-4 px-4 pb-8 md:px-6 lg:px-8">
        {isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : !d ? null : (
          <>
            {/* --- las cifras que deciden si se puede entregar ------------ */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <Tile titulo="ASIENTOS" valor={d.asientos} tono="acc" />
              <Tile
                titulo="LÍNEAS CONCAR"
                subtitulo="dos por asiento"
                valor={d.lineas}
                tono="acc"
              />
              <Tile
                titulo="ERRORES"
                subtitulo={d.errores ? "impiden entregar" : "ninguno"}
                valor={d.errores}
                tono={d.errores ? "bad" : "ok"}
              />
              <Tile
                titulo="AVISOS"
                subtitulo={d.avisos ? "dejan pasar" : "ninguno"}
                valor={d.avisos}
                tono={d.avisos ? "warn" : "ok"}
              />
            </div>

            {/* El cuadre, UNA TARJETA POR MONEDA. Sumar soles y dólares en la
                misma cifra da un total que no existe, y este es justo el
                número que decide si el archivo se entrega. */}
            <div className="grid gap-3 sm:grid-cols-2">
              {monedas.map((m) => (
                <Tile
                  key={m.moneda}
                  titulo={`DEBE − HABER · ${nombreMoneda(m.moneda).toUpperCase()}`}
                  subtitulo={`${m.asientos} asientos · tiene que ser cero`}
                  valor={dinero(m.debe - m.haber)}
                  tono={m.cuadra ? "ok" : "bad"}
                  pie={
                    <>
                      debe {dinero(m.debe)} · haber {dinero(m.haber)}
                    </>
                  }
                />
              ))}
            </div>

            {/* --- el veredicto, en una línea ----------------------------- */}
            {d.asientos === 0 ? (
              <Aviso tono="info">
                <Info className="mt-px size-4 shrink-0" />
                <span>
                  <b>No hay ningún cruce confirmado en este periodo.</b> El
                  archivo saldría con la forma correcta y sin una sola línea.
                </span>
              </Aviso>
            ) : puedeEntregar ? (
              <Aviso tono="info">
                <CheckCircle2 className="mt-px size-4 shrink-0" />
                <span>
                  <b>El archivo se puede entregar.</b> {d.asientos} asientos,{" "}
                  {d.lineas} líneas, del comprobante {d.desde} al {d.hasta}, y
                  el debe y el haber cuadran exacto en{" "}
                  {monedas.map((m) => nombreMoneda(m.moneda)).join(" y ")}.
                </span>
              </Aviso>
            ) : (
              <Aviso tono="bad">
                <XCircle className="mt-px size-4 shrink-0" />
                <span>
                  <b>Este archivo no se puede entregar todavía.</b> Hay{" "}
                  {d.errores} línea(s) con un dato que falta, y CONCAR las
                  rechazaría o las asentaría contra la cuenta equivocada.
                </span>
              </Aviso>
            )}

            {d.arranqueSinConfirmar > 0 ? (
              <Aviso tono="warn">
                <AlertTriangle className="mt-px size-4 shrink-0" />
                <span>
                  <b>La numeración arranca en 1 por suposición.</b> Nadie ha
                  dicho todavía qué periodos se entregaron a CONCAR con el Excel
                  o con el ERP del área, ni con qué último número. Si esos
                  comprobantes existen, estos los repiten.
                </span>
              </Aviso>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-2">
              {/* --- validación ----------------------------------------- */}
              <Caja
                titulo="Validación"
                derecha={`${d.errores} errores · ${d.avisos} avisos`}
              >
                {d.problemas.length === 0 ? (
                  <p className="text-[12.5px] text-muted-foreground">
                    Sin observaciones. Todo lo que va a salir tiene su cuenta,
                    su anexo y su documento.
                  </p>
                ) : (
                  <ul className="space-y-2.5">
                    {(d.problemas as Problema[]).map((p) => (
                      <li key={p.codigo} className="text-[12.5px] leading-snug">
                        <span
                          className={cn(
                            "mr-2 rounded px-1.5 py-0.5 font-semibold text-[10px] uppercase tracking-wide",
                            p.nivel === "error"
                              ? "bg-red-500/15 text-red-700 dark:text-red-400"
                              : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
                          )}
                        >
                          {p.nivel}
                        </span>
                        {p.mensaje}
                        <span className="text-muted-foreground">
                          {" "}
                          · {p.cuantos}{" "}
                          {p.cuantos === 1 ? "comprobante" : "comprobantes"}
                          {p.ejemplos.length > 0
                            ? `: ${p.ejemplos.join(", ")}${p.cuantos > p.ejemplos.length ? "…" : ""}`
                            : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Caja>

              {/* --- cómo se arma, y qué se entregó ---------------------- */}
              <Caja titulo="Cómo se arma el asiento">
                <div className="space-y-3 text-[12.5px] leading-relaxed">
                  <p>
                    <b>Ingreso</b> (sub diario 21): la cuenta del banco al{" "}
                    <i>debe</i> por lo que entró, y la cuenta por cobrar del
                    cliente al <i>haber</i>. El anexo del banco es un código
                    propio de la cuenta; el del cliente es su DNI o su RUC.
                  </p>
                  <p className="text-muted-foreground">
                    El número de comprobante son dos dígitos de mes y cuatro de
                    correlativo. Se reserva al descargar, no al mirar, y sigue
                    por empresa, sub diario y periodo: dos descargas del mismo
                    mes no se pisan.
                  </p>
                  {d.entregas.length > 0 ? (
                    <div className="border-t pt-3">
                      <p className="mb-2 font-semibold">Ya entregado</p>
                      <ul className="space-y-1.5">
                        {(d.entregas as Entrega[]).map((e) => (
                          <li
                            key={`${e.archivo}-${e.cuando}`}
                            className="text-[11.5px] text-muted-foreground"
                          >
                            Sub diario {e.subDiario} · {e.desde}–{e.hasta} ·{" "}
                            {e.lineas} líneas · {e.usuario}
                            {e.forzada ? (
                              <b className="text-amber-600"> · con errores</b>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="border-t pt-3 text-muted-foreground">
                      De este periodo no se ha entregado nada todavía.
                    </p>
                  )}
                </div>
              </Caja>
            </div>

            {/* --- las líneas, tal como van a salir --------------------- */}
            <Caja
              titulo="Las líneas que van a salir"
              derecha={`${d.muestra.length} de ${d.lineas}`}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] tabular-nums">
                  <thead>
                    <tr className="border-b text-left text-[10.5px] text-muted-foreground uppercase tracking-wide">
                      <th className="py-1.5 pr-3 font-medium">Sub</th>
                      <th className="py-1.5 pr-3 font-medium">Comprobante</th>
                      <th className="py-1.5 pr-3 font-medium">Fecha</th>
                      <th className="py-1.5 pr-3 font-medium">Mon.</th>
                      <th className="py-1.5 pr-3 font-medium">Cuenta</th>
                      <th className="py-1.5 pr-3 font-medium">Anexo</th>
                      <th className="py-1.5 pr-3 font-medium">D/H</th>
                      <th className="py-1.5 pr-3 text-right font-medium">
                        Importe
                      </th>
                      <th className="py-1.5 pr-3 font-medium">TD</th>
                      <th className="py-1.5 pr-3 font-medium">N.º doc.</th>
                      <th className="py-1.5 pr-3 font-medium">Área</th>
                      <th className="py-1.5 font-medium">Glosa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.muestra as Linea[]).map((l, i) => (
                      <tr
                        key={`${l.numeroComprobante}-${l.debeHaber}-${i}`}
                        className={cn(
                          "border-b last:border-0",
                          // La primera línea de cada asiento abre grupo: sin
                          // esto, 200 filas seguidas no dejan ver dónde
                          // empieza y acaba un comprobante.
                          l.debeHaber === "D" && "border-t-2 border-t-border",
                        )}
                      >
                        <td className="py-1.5 pr-3">{l.subDiario}</td>
                        <td className="py-1.5 pr-3 font-medium">
                          {l.numeroComprobante}
                        </td>
                        <td className="py-1.5 pr-3">{l.fechaComprobante}</td>
                        <td className="py-1.5 pr-3">{l.codigoMoneda}</td>
                        <td
                          className={cn(
                            "py-1.5 pr-3",
                            !l.cuentaContable &&
                              "bg-red-500/10 font-semibold text-red-700 dark:text-red-400",
                          )}
                        >
                          {l.cuentaContable || "falta"}
                        </td>
                        <td
                          className={cn(
                            "py-1.5 pr-3",
                            !l.codigoAnexo && "text-amber-600",
                          )}
                        >
                          {l.codigoAnexo ?? "—"}
                        </td>
                        <td className="py-1.5 pr-3 font-semibold">
                          {l.debeHaber}
                        </td>
                        <td className="py-1.5 pr-3 text-right">
                          {l.importe.toLocaleString("es-PE", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="py-1.5 pr-3">
                          {l.tipoDocumento ?? "—"}
                        </td>
                        <td
                          className={cn(
                            "py-1.5 pr-3",
                            !l.numeroDocumento && "text-amber-600",
                          )}
                        >
                          {l.numeroDocumento ?? "—"}
                        </td>
                        <td className="py-1.5 pr-3">{l.codigoArea ?? "—"}</td>
                        <td className="py-1.5 text-muted-foreground">
                          {l.glosaPrincipal}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Caja>
          </>
        )}
      </div>
    </>
  );
}
